import { Folder } from "../types";
import { SimpleMenu } from "@kaltura/mediaspace-shared-ui";
import { MoreVertical16Icon } from "@kaltura/ds-react-icons";
import { IconButton, MenuItem } from "@kaltura/ds-react-components";
import { translate } from "@kaltura/mediaspace-shared-utils";
import { useState } from "react";
import EditFolderModal from "../edit-folder-modal/EditFolderModal";
import DeleteFolderModal from "../delete-folder-modal/DeleteFolderModal";
import { ButtonAnalyticsProvider, useButtonAnalytics } from "@kaltura/mediaspace-shared-hooks";
import { ButtonClickAnalyticsType } from "@kaltura/mediaspace-shared-types";

export interface FolderActionsMenuProps {
    /** currently selected folder in the filter */
    selectedFolder: Folder;
    /** callback to execute after the delete folder request was submitted */
    onDeleteSubmitEnd?: () => void;
}

export function FolderActionsMenu({ selectedFolder, onDeleteSubmitEnd }: FolderActionsMenuProps) {
    const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
    const [closeMenu, setCloseMenu] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

    const sendButtonAnalytics = useButtonAnalytics();

    const handleOpenUpdateModal = () => {
        sendButtonAnalytics("Folder actions menu - Update", ButtonClickAnalyticsType.EDIT);
        setIsUpdateModalOpen(true);
    };

    const handleCloseUpdateModal = () => {
        setIsUpdateModalOpen(false);
    };

    const handleOpenDeleteModal = () => {
        sendButtonAnalytics("Folder actions menu - Delete", ButtonClickAnalyticsType.DELETE);
        setIsDeleteModalOpen(true);
    };

    const handleCloseDeleteModal = () => {
        setIsDeleteModalOpen(false);
    };

    const handleOpenMenu = () => {
        sendButtonAnalytics("Folder actions menu - Open", ButtonClickAnalyticsType.EXPAND);
        setCloseMenu(false);
    };

    const handleCloseMenu = () => {
        setCloseMenu(true);
    };

    const handleUpdateEnded = () => {
        handleCloseUpdateModal();
        handleCloseMenu();
    };

    const handleDeleteEnded = () => {
        handleCloseDeleteModal();
        handleCloseMenu();
        onDeleteSubmitEnd?.();
    };

    return (
        <>
            <SimpleMenu
                id={"folderActions"}
                button={<IconButton color={"secondary"} size={"small"} aria-label={translate("Folder actions")} />}
                buttonIcon={<MoreVertical16Icon />}
                close={closeMenu}
                onOpen={handleOpenMenu}
            >
                <MenuItem key={"update-folder"} onClick={handleOpenUpdateModal}>
                    {translate("Update folder")}
                </MenuItem>
                <MenuItem key={"delete-folder"} onClick={handleOpenDeleteModal}>
                    {translate("Delete folder")}
                </MenuItem>
            </SimpleMenu>
            <ButtonAnalyticsProvider location={"Modal - Update folder"} applicationFeature={"folders"}>
                {isUpdateModalOpen && (
                    <EditFolderModal
                        currentFolder={selectedFolder}
                        isOpen={isUpdateModalOpen}
                        onClose={handleCloseUpdateModal}
                        onSubmitEnd={handleUpdateEnded}
                    />
                )}
            </ButtonAnalyticsProvider>
            <ButtonAnalyticsProvider location={"Modal - Delete folder"} applicationFeature={"folders"}>
                <DeleteFolderModal
                    folder={selectedFolder}
                    isOpen={isDeleteModalOpen}
                    onClose={handleCloseDeleteModal}
                    onSubmitEnd={handleDeleteEnded}
                />
            </ButtonAnalyticsProvider>
        </>
    );
}

export default FolderActionsMenu;
